import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Filter, Grid, List, ChevronDown, Shield, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Slider } from "../components/ui/slider";
import ProductCard from "../components/ProductCard";
import VehicleFilter from "../components/VehicleFilter";
import axios from "axios";
import { API } from "../App";

// ============================================
// FEATURE FLAG - Vehicle Search Visibility on Shop Page
// Set to true to show vehicle search, false to hide
// This allows easy reactivation in the future
// ============================================
const SHOW_VEHICLE_SEARCH = false;

const ShopPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(category || "");
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [sortBy, setSortBy] = useState("featured");
  const [vehicleFilter, setVehicleFilter] = useState({
    year: searchParams.get("year") || "",
    make: searchParams.get("make") || "",
    model: searchParams.get("model") || "",
    engine: searchParams.get("engine") || "",
  });

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Update selected category when URL changes
  useEffect(() => {
    setSelectedCategory(category || "");
  }, [category]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: 12,
        };

        if (selectedCategory) params.category = selectedCategory;
        if (search) params.search = search;
        if (priceRange[0] > 0) params.min_price = priceRange[0];
        if (priceRange[1] < 2000) params.max_price = priceRange[1];
        if (vehicleFilter.year) params.year = vehicleFilter.year;
        if (vehicleFilter.make) params.make = vehicleFilter.make;
        if (vehicleFilter.model) params.model = vehicleFilter.model;
        if (vehicleFilter.engine) params.engine = vehicleFilter.engine;

        const response = await axios.get(`${API}/products`, { params });
        
        let sortedProducts = [...response.data.products];
        
        // Sort products
        switch (sortBy) {
          case "price-low":
            sortedProducts.sort((a, b) => a.price - b.price);
            break;
          case "price-high":
            sortedProducts.sort((a, b) => b.price - a.price);
            break;
          case "name":
            sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
            break;
          case "featured":
          default:
            sortedProducts.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        }

        setProducts(sortedProducts);
        setTotalProducts(response.data.total);
        setTotalPages(response.data.pages);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedCategory, search, priceRange, sortBy, currentPage, vehicleFilter]);

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
    if (cat) {
      navigate(`/shop/${cat}`);
    } else {
      navigate("/shop");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleVehicleFilter = (filter) => {
    setVehicleFilter(filter);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearch("");
    setSelectedCategory("");
    setPriceRange([0, 2000]);
    setVehicleFilter({ year: "", make: "", model: "", engine: "" });
    setCurrentPage(1);
    navigate("/shop");
  };

  const getCategoryName = () => {
    if (!selectedCategory) return "All Products";
    const cat = categories.find(c => c.slug === selectedCategory);
    return cat ? cat.name : selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1);
  };

  const FilterSidebar = () => (
    <div className="space-y-8">
      {/* Categories */}
      <div>
        <h3 className="text-white font-heading text-lg uppercase tracking-wider mb-4">Categories</h3>
        <div className="space-y-2">
          <button
            onClick={() => handleCategoryChange("")}
            className={`block w-full text-left py-2 px-3 rounded transition-colors ${
              !selectedCategory ? "bg-primary text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800"
            }`}
            data-testid="filter-all"
          >
            All Products
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => handleCategoryChange(cat.slug)}
              className={`block w-full text-left py-2 px-3 rounded transition-colors ${
                selectedCategory === cat.slug ? "bg-primary text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800"
              }`}
              data-testid={`filter-${cat.slug}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="text-white font-heading text-lg uppercase tracking-wider mb-4">Price Range</h3>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          max={2000}
          step={50}
          className="mb-4"
          data-testid="price-slider"
        />
        <div className="flex items-center justify-between text-zinc-400 text-sm">
          <span>${priceRange[0]}</span>
          <span>${priceRange[1]}+</span>
        </div>
      </div>

      {/* Clear Filters */}
      <Button
        variant="outline"
        onClick={clearAllFilters}
        className="w-full border-zinc-700 text-zinc-400 hover:text-white"
        data-testid="clear-filters-btn"
      >
        Clear All Filters
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="shop-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-12">
        <div className="container mx-auto px-4">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight" data-testid="page-title">
            {getCategoryName()}
          </h1>
          <p className="text-zinc-400 mt-2">{totalProducts} products found</p>
        </div>
      </div>

      {/* Vehicle Filter - HIDDEN BUT PRESERVED (Feature Flag Controlled) */}
      {SHOW_VEHICLE_SEARCH && (
        <div className="bg-zinc-900 border-b border-zinc-800">
          <div className="container mx-auto px-4 py-6">
            <VehicleFilter onFilter={handleVehicleFilter} />
          </div>
        </div>
      )}

      {/* Premium Promotional Banner - Replaces Vehicle Search */}
      {!SHOW_VEHICLE_SEARCH && (
        <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border-b border-zinc-700">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif" }}>
                    Best Prices. Quality You Can Trust.
                  </h2>
                  <p className="text-zinc-400 text-sm mt-1 tracking-wide">
                    Premium accessories with exceptional value
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-zinc-500">
                <Shield className="w-5 h-5 text-green-500" />
                <span className="text-sm">100% Quality Guaranteed</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-32">
              <FilterSidebar />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <form onSubmit={handleSearch} className="flex-1 max-w-md">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-white pr-20"
                    data-testid="search-input"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 bg-primary hover:bg-primary/90"
                    data-testid="search-submit"
                  >
                    Search
                  </Button>
                </div>
              </form>

              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden border-zinc-700" data-testid="mobile-filter-btn">
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="bg-zinc-900 border-zinc-800">
                    <SheetHeader>
                      <SheetTitle className="text-white font-heading uppercase">Filters</SheetTitle>
                    </SheetHeader>
                    <div className="mt-8">
                      <FilterSidebar />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-700 text-white" data-testid="sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="featured" className="text-white">Featured</SelectItem>
                    <SelectItem value="price-low" className="text-white">Price: Low to High</SelectItem>
                    <SelectItem value="price-high" className="text-white">Price: High to Low</SelectItem>
                    <SelectItem value="name" className="text-white">Name: A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-zinc-900 aspect-[3/4] skeleton rounded"></div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20" data-testid="no-products">
                <p className="text-zinc-400 text-lg">No products found matching your criteria.</p>
                <Button onClick={clearAllFilters} className="mt-4 btn-primary">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                  {products.map((product) => (
                    <ProductCard key={product.product_id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12" data-testid="pagination">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="border-zinc-700"
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-4 text-zinc-400">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="border-zinc-700"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
